import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { CaseDetailPanel } from '../components/CaseDetailPanel';
import { CallWorkspace } from '../components/CallWorkspace';
import { PrescriptionForm } from '../components/PrescriptionForm';

export default function CaseSession() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [session, setSession] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Step 1: Handle "new" session creation
  useEffect(() => {
    if (sessionId === 'new') {
      const caseId = searchParams.get('caseId');
      if (!caseId) {
        setError('Missing case ID to start a new session.');
        setLoading(false);
        return;
      }

      const createSession = async () => {
        try {
          const apiUrl = process.env.REACT_APP_BACKEND_URL;
          const res = await axios.post(`${apiUrl}/api/v1/clinicalsim/sessions`, { case_id: caseId }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Redirect to the actual session ID
          navigate(`/cs/sessions/${res.data.session_id}`, { replace: true });
        } catch (err) {
          console.error('Failed to create session:', err);
          setError(err.response?.data?.detail || 'Failed to create session. Please try again.');
          setLoading(false);
        }
      };
      createSession();
    }
  }, [sessionId, searchParams, navigate, token]);

  // Step 2: Load existing session and case data
  useEffect(() => {
    if (!sessionId || sessionId === 'new' || !token) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        
        // 1. Fetch Session
        const sessionRes = await axios.get(`${apiUrl}/api/v1/clinicalsim/sessions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const currentSession = sessionRes.data.sessions.find(s => s.session_id === sessionId);
        
        if (!currentSession) {
          throw new Error('Session not found');
        }
        setSession(currentSession);

        // 2. Fetch Case Details
        const caseRes = await axios.get(`${apiUrl}/api/v1/clinicalsim/cases/${currentSession.case_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCaseData(caseRes.data);
        setError('');
      } catch (err) {
        console.error('Failed to load session/case:', err);
        setError('Session not found or access denied.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [sessionId, token]);

  if (loading || sessionId === 'new') {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
      </div>
    );
  }

  if (error || !caseData || !session) {
    return (
      <div className="p-8 max-w-2xl mx-auto mt-10 bg-red-50 border border-red-100 rounded-2xl text-center">
        <h2 className="text-lg font-bold text-red-600 mb-2">Error Loading Case</h2>
        <p className="text-red-500">{error || 'Unknown error occurred.'}</p>
        <button 
          onClick={() => navigate('/cs/library')}
          className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-xl font-medium hover:bg-red-200"
        >
          Return to Library
        </button>
      </div>
    );
  }

  const isCompleted = session.status === 'completed';

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel: Case Details */}
        <div className="w-1/3 min-w-[350px] max-w-[450px] border-r border-slate-200 bg-white overflow-y-auto">
          <CaseDetailPanel caseData={caseData} session={session} />
        </div>
        
        {/* Right Panel: Workspace (Call or Prescription/Scorecard) */}
        <div className="flex-1 flex flex-col bg-slate-50 overflow-y-auto relative">
          {isCompleted ? (
            <PrescriptionForm session={session} caseData={caseData} />
          ) : (
            <CallWorkspace 
              session={session} 
              caseData={caseData} 
              onCallEnd={() => {
                // Refresh session data to reflect 'completed' status
                setSession(prev => ({ ...prev, status: 'completed' }));
              }} 
            />
          )}
        </div>
      </div>
    </div>
  );
}
